import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Building2, Phone, Mail, Clock, Search, Package, Truck, Activity, CheckCircle, AlertTriangle, Loader2,
  Warehouse, MapPin, Calendar, Users, Sprout, Pill, ShoppingBag,
  Shirt, ArrowRight, ShieldCheck, Globe, BarChart3
} from 'lucide-react';
import branchData from '../data/branches.json';
import {
  getTrackingData,
  getStatusInfo,
  processTrackingHistory,
  getApiEndpoint,
  validateTrackingNumber
} from '../utils/trackingUtils';

import NoticeBanner from '../components/NoticeBanner';

// --- ANIMATION VARIANTS ---

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 }
  }
};

const expandResult = {
  hidden: { opacity: 0, height: 0, scale: 0.99 },
  visible: {
    opacity: 1,
    height: "auto",
    scale: 1,
    transition: { duration: 0.4, ease: "circOut" }
  },
  exit: { opacity: 0, height: 0, scale: 0.99, transition: { duration: 0.2 } }
};

// --- KEYFRAMES ---

const spinAnimation = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// --- STYLED COMPONENTS ---

const PageWrapper = styled.div`
  margin-top: 80px;
  min-height: calc(100vh - 80px);
  background-color: #F8FAFC; /* Slate-50 */
  font-family: 'Inter', sans-serif;
  color: #0f172a; /* Slate-900 */
  overflow-x: hidden;

  &.with-notice {
    margin-top: 132px;
  }
`;

// --- HERO SECTION ---

const HeroSection = styled.section`
  position: relative;
  min-height: 80vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 24px;
  background-image: linear-gradient(180deg, #0f172a 0%, rgba(15, 23, 42, 0.9) 100%),
    url('https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?q=80&w=2070&auto=format&fit=crop');
  background-size: cover;
  background-position: center;
  color: white;
  text-align: center;
`;

const HeroContent = styled.div`
  width: 100%;
  max-width: 900px;
  z-index: 10;
`;

const HeroTitle = styled(motion.h1)`
  font-size: clamp(2.5rem, 5vw, 4.5rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.1;
  margin-bottom: 24px;
  color: white;
  
  span {
    color: #818cf8; /* Indigo-400 */
    display: inline-block;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: clamp(1.1rem, 2vw, 1.25rem);
  color: #cbd5e1;
  margin-bottom: 48px;
  max-width: 650px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  font-weight: 400;
`;

// --- SEARCH BAR ---

const SearchContainer = styled(motion.div)`
  width: 100%;
  max-width: 680px;
  margin: 0 auto;
  position: relative;
`;

const SearchForm = styled.form`
  display: flex;
  align-items: center;
  background: #ffffff; 
  padding: 8px;
  border-radius: 100px;
  border: 1px solid #e2e8f0; 
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  transition: all 0.3s ease;

  &:focus-within {
    border-color: #6366f1;
    box-shadow: 0 25px 50px -12px rgba(79, 70, 229, 0.25);
    transform: translateY(-2px);
  }
`;

const SearchInput = styled.input`
  flex: 1;
  background: transparent;
  border: none;
  padding: 16px 24px;
  font-size: 1.1rem;
  color: #0f172a;
  font-weight: 500;
  outline: none;

  &::placeholder {
    color: #94a3b8;
  }
`;

const SearchBtn = styled.button`
  background: #0f172a;
  color: white;
  border: none;
  border-radius: 50px;
  padding: 14px 32px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.2s ease;

  &:hover {
    background: #334155;
  }

  &:disabled {
    background: #94a3b8;
    cursor: not-allowed;
  }
`;

// **FIX:** Created a styled component for the Loader
const Spinner = styled(Loader2)`
  animation: ${spinAnimation} 1s linear infinite;
`;

// --- TRACKING RESULTS ---

const ResultCard = styled(motion.div)`
  margin-top: 24px;
  background: #ffffff;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  border: 1px solid #e2e8f0;
  text-align: left;
`;

const ResultHeader = styled.div`
  padding: 24px 32px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;

  .label { 
    font-size: 0.85rem; 
    text-transform: uppercase; 
    letter-spacing: 0.05em; 
    color: #64748b; 
    font-weight: 700; 
    margin-bottom: 4px; 
  }
  .value { 
    font-size: 1.25rem; 
    font-weight: 800; 
    color: #0f172a; 
    font-family: monospace;
  }
  
  .badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #eff6ff;
    color: #1d4ed8;
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 0.95rem;
    font-weight: 700;
    border: 1px solid #dbeafe;
  }
`;

const Timeline = styled.div`
  padding: 32px;
  max-height: 400px;
  overflow-y: auto;
  background: white;
`;

const TimelineItem = styled(motion.div)`
  display: flex;
  gap: 20px;
  position: relative;
  padding-bottom: 32px;

  &:last-child { padding-bottom: 0; }
  
  &:not(:last-child)::before {
    content: '';
    position: absolute;
    left: 20px;
    top: 40px;
    bottom: 0;
    width: 2px;
    background: #e2e8f0;
  }

  .icon-wrapper {
    position: relative;
    z-index: 2;
    width: 42px;
    height: 42px;
    border-radius: 50%;
    background: ${props => props.$active ? '#0f172a' : '#f1f5f9'};
    border: 2px solid ${props => props.$active ? '#0f172a' : '#e2e8f0'};
    color: ${props => props.$active ? 'white' : '#94a3b8'};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .content {
    padding-top: 6px;
    h4 { 
      font-size: 1.05rem; 
      font-weight: 700; 
      color: #0f172a; 
      margin-bottom: 4px; 
    }
    p { 
      font-size: 0.95rem; 
      color: #475569; 
    }
    .time { 
      font-size: 0.85rem; 
      color: #94a3b8; 
      display: block; 
      margin-top: 6px; 
      font-weight: 500;
    }
  }
`;

const ErrorMessage = styled(motion.div)`
  margin-top: 16px;
  background: #fef2f2;
  border: 1px solid #fee2e2;
  color: #991b1b;
  padding: 16px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-weight: 600;
  justify-content: center;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
`;

// --- SECTIONS ---

const Section = styled.section`
  padding: 100px 0;
  background: ${props => props.$dark ? '#0f172a' : props.$bg || 'white'};
  color: ${props => props.$dark ? 'white' : '#0f172a'};
  border-bottom: 1px solid ${props => props.$dark ? '#1e293b' : '#f1f5f9'};
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
`;

const SectionHeader = styled(motion.div)`
  text-align: center;
  margin-bottom: 80px;
  
  h2 {
    font-size: clamp(2rem, 4vw, 3rem);
    font-weight: 800;
    margin-bottom: 16px;
    letter-spacing: -0.02em;
    color: ${props => props.$dark ? 'white' : '#0f172a'};
  }
  
  p {
    color: ${props => props.$dark ? '#cbd5e1' : '#475569'};
    font-size: 1.25rem;
    max-width: 700px;
    margin: 0 auto;
    line-height: 1.6;
    font-weight: 400;
  }
`;

// --- STATS GRID ---
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1px;
  background: #cbd5e1;
  border: 1px solid #cbd5e1;
  border-radius: 16px;
  overflow: hidden;

  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const StatItem = styled.div`
  background: white;
  padding: 40px;
  text-align: center;
  
  .icon { color: #4f46e5; margin-bottom: 16px; width: 32px; height: 32px; margin: 0 auto 16px; }
  .num { font-size: 2.5rem; font-weight: 800; color: #0f172a; margin-bottom: 4px; display: block; }
  .label { color: #64748b; font-size: 0.9rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; }
`;

// --- GRID CARDS ---

const Grid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: 32px;
`;

const FeatureCard = styled(motion.div)`
  padding: 40px;
  background: ${props => props.$dark ? '#1e293b' : 'white'};
  border: 1px solid ${props => props.$dark ? '#334155' : '#e2e8f0'};
  border-radius: 16px;
  transition: all 0.2s ease;
  cursor: default;

  &:hover {
    transform: translateY(-5px);
    border-color: #6366f1;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    
    .icon {
      color: #6366f1;
      background: ${props => props.$dark ? 'rgba(99, 102, 241, 0.2)' : '#eef2ff'};
    }
  }

  .icon {
    width: 48px;
    height: 48px;
    background: ${props => props.$dark ? '#334155' : '#f1f5f9'};
    padding: 10px;
    border-radius: 12px;
    color: ${props => props.$dark ? '#cbd5e1' : '#0f172a'};
    margin-bottom: 24px;
    transition: all 0.3s ease;
  }

  h3 {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 12px;
    color: ${props => props.$dark ? 'white' : '#0f172a'};
  }

  p {
    color: ${props => props.$dark ? '#cbd5e1' : '#475569'};
    line-height: 1.7;
    font-size: 1.05rem;
  }
`;

const CTASection = styled.section`
  padding: 100px 24px;
  background: #0f172a;
  text-align: center;
  border-top: 1px solid #1e293b;
  
  h2 { font-size: 3rem; color: white; margin-bottom: 24px; font-weight: 800; }
  p { color: #cbd5e1; font-size: 1.25rem; margin-bottom: 40px; }
`;

const CTALink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: #4f46e5;
  color: white;
  padding: 18px 48px;
  border-radius: 50px;
  font-weight: 700;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: #4338ca;
    transform: translateY(-2px);
    box-shadow: 0 10px 15px -3px rgba(79, 70, 229, 0.4);
  }
`;

// --- COMPONENT ---
// --- WHY US SECTION STYLES ---

const WhySection = styled.section`
  padding: 120px 0;
  background: white;
  position: relative;
  overflow: hidden;
`;

const WhyContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: center;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 60px;
  }
`;

const WhyContent = styled(motion.div)`
  h2 {
    font-size: clamp(2.5rem, 4vw, 3.5rem);
    font-weight: 800;
    color: #0f172a;
    margin-bottom: 24px;
    line-height: 1.1;
  }

  p.subtitle {
    font-size: 1.15rem;
    color: #64748b;
    margin-bottom: 48px;
    line-height: 1.6;
  }
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FeatureItem = styled(motion.div)`
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 24px;
  border-radius: 20px;
  background: #f8fafc; /* Light Slate bg */
  border: 1px solid #f1f5f9;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: default;

  &:hover {
    background: white;
    border-color: #6366f1; /* Indigo Border on hover */
    box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.08);
    transform: translateX(10px);

    .icon-box {
      background: #4f46e5;
      color: white;
      transform: scale(1.1) rotate(-5deg);
    }
  }

  .icon-box {
    width: 52px;
    height: 52px;
    background: #eef2ff; /* Very light Indigo */
    color: #4f46e5; /* Indigo */
    border-radius: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all 0.3s ease;
  }

  div {
    h4 {
      font-size: 1.2rem;
      font-weight: 700;
      color: #0f172a;
      margin-bottom: 6px;
    }
    p {
      font-size: 0.95rem;
      color: #64748b;
      line-height: 1.5;
      margin: 0;
    }
  }
`;

const WhyVisual = styled(motion.div)`
  position: relative;
  height: 600px;
  border-radius: 32px;
  
  @media (max-width: 968px) {
    height: 400px;
    order: -1; /* Puts image on top on mobile */
  }
`;

const MainImage = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 32px;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.7s ease;
  }

  &:hover img {
    transform: scale(1.03);
  }
`;

const FloatingBadge = styled(motion.div)`
  position: absolute;
  bottom: 40px;
  left: -40px;
  background: white;
  padding: 24px 32px;
  border-radius: 24px;
  box-shadow: 0 20px 40px -5px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  gap: 20px;
  border: 1px solid #f1f5f9;
  z-index: 2;

  @media (max-width: 1200px) {
    left: -20px;
  }
  @media (max-width: 968px) {
    left: 20px;
    bottom: 20px;
    padding: 16px 24px;
  }

  .stat-icon {
    width: 56px;
    height: 56px;
    background: #10b981; /* Emerald Green */
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    box-shadow: 0 10px 15px -3px rgba(16, 185, 129, 0.3);
  }

  .stat-text {
    h3 {
      font-size: 1.8rem;
      font-weight: 800;
      color: #0f172a;
      margin: 0;
      line-height: 1;
    }
    span {
      color: #64748b;
      font-size: 0.85rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      margin-top: 4px;
      display: block;
    }
  }
`;

// --- NETWORK SECTION STYLES ---

const NetworkSection = styled.section`
  padding: 120px 0;
  background-color: #f8fafc;
`;

const NetworkGrid = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr 1fr;
  grid-template-rows: auto auto;
  gap: 32px;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 24px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

// Dark Card for Head Office
const HeadOfficeCard = styled(motion.div)`
  grid-row: span 2; /* Takes up full height on left */
  background: #0f172a; /* Slate-900 */
  border-radius: 32px;
  padding: 48px;
  color: white;
  position: relative;
  overflow: hidden;
  box-shadow: 0 25px 50px -12px rgba(15, 23, 42, 0.25);

  /* Decorative Pattern */
  &::before {
    content: '';
    position: absolute;
    top: 0; right: 0;
    width: 300px;
    height: 300px;
    background: radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 70%);
    border-radius: 0 0 0 100%;
  }

  h3 { font-size: 2rem; margin-bottom: 8px; color: white; }
  .badge { 
    display: inline-block; 
    padding: 6px 12px; 
    background: #4f46e5; 
    color: white; 
    border-radius: 50px; 
    font-size: 0.8rem; 
    font-weight: 700; 
    margin-bottom: 32px; 
    letter-spacing: 0.05em;
  }

  .detail-row {
    display: flex;
    gap: 16px;
    margin-bottom: 24px;
    align-items: flex-start;
    
    .icon { color: #818cf8; flex-shrink: 0; margin-top: 4px; }
    
    div {
      h5 { color: #94a3b8; font-size: 0.9rem; margin-bottom: 4px; text-transform: uppercase; letter-spacing: 0.05em; }
      p { font-size: 1.1rem; line-height: 1.5; color: #f1f5f9; }
    }
  }

  @media (max-width: 1024px) {
    grid-row: auto;
    padding: 32px;
  }
`;

// Light Cards for Branches
const BranchCard = styled(motion.div)`
  background: white;
  border-radius: 24px;
  padding: 32px;
  border: 1px solid #e2e8f0;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: center;

  &:hover {
    transform: translateY(-5px);
    border-color: #cbd5e1;
    box-shadow: 0 10px 20px -5px rgba(0,0,0,0.05);
  }

  h4 { font-size: 1.25rem; font-weight: 700; color: #0f172a; margin-bottom: 20px; }

  .mini-detail {
    display: flex;
    gap: 12px;
    margin-bottom: 12px;
    align-items: flex-start;
    font-size: 0.95rem;
    color: #64748b;

    .icon { color: #6366f1; width: 18px; flex-shrink: 0; margin-top: 2px; }
  }
`;

const Home = () => {
  const [hasNotices, setHasNotices] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingResult, setTrackingResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // --- Tracking Logic ---
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!trackingNumber.trim()) return;

    if (!validateTrackingNumber(trackingNumber)) {
      setError('Format Error: Use format like 123456SDD7890');
      setTrackingResult(null);
      return;
    }

    setIsLoading(true);
    setError(null);
    setTrackingResult(null);

    try {
      const localData = getTrackingData(trackingNumber);
      if (localData) {
        setTrackingResult(localData);
      } else {
        const response = await fetch(`${getApiEndpoint('searchById')}/${trackingNumber.trim()}`);
        const data = await response.json();
        if (data.success) {
          setTrackingResult(data.data);
        } else {
          setError(data.message || 'Shipment not found.');
        }
      }
    } catch (err) {
      setError('Connection failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Map backend icon string to Lucide component
  const getIcon = (iconName) => {
    const map = {
      'FaBox': Package,
      'FaTruck': Truck,
      'FaRunning': Activity,
      'FaCheckCircle': CheckCircle,
      'FaExclamationTriangle': AlertTriangle
    };
    return map[iconName] || Package;
  };

  return (
    <PageWrapper className={hasNotices ? 'with-notice' : ''}>
      <NoticeBanner onNoticesChange={setHasNotices} />

      {/* HERO & SEARCH */}
      <HeroSection>
        <HeroContent>
          <HeroTitle
            initial="hidden" animate="visible" variants={fadeInUp}
          >
            The Future of <br />
            <span>Connected Logistics</span>
          </HeroTitle>
          <HeroSubtitle
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3, duration: 1 }}
          >
            Seamlessly bridging the gap between warehouses and doorsteps.
            Track shipments, manage inventory, and scale your business with SDD.
          </HeroSubtitle>

          <SearchContainer initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.5 }}>
            <SearchForm onSubmit={handleSearch}>
              <SearchInput
                placeholder="Enter Tracking ID (e.g., 599663SDD2442)..."
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
              />
              <SearchBtn type="submit" disabled={isLoading}>
                {/* Use the new Styled Component Spinner */}
                {isLoading ? <Spinner size={20} /> : <Search size={20} />}
                <span>Track</span>
              </SearchBtn>
            </SearchForm>

            <AnimatePresence>
              {error && (
                <ErrorMessage initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                  <AlertTriangle size={20} /> {error}
                </ErrorMessage>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {trackingResult && (
                <ResultCard
                  variants={expandResult}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                >
                  <ResultHeader>
                    <div>
                      <div className="label">Shipment ID</div>
                      <div className="value">{trackingResult.shipment_id}</div>
                    </div>
                    <div className="badge">
                      <Activity size={16} />
                      {getStatusInfo(trackingResult.status).title}
                    </div>
                  </ResultHeader>
                  <Timeline>
                    {processTrackingHistory(trackingResult.history, trackingResult.status).map((step, idx) => {
                      const Icon = getIcon(step.icon);
                      return (
                        <TimelineItem
                          key={idx}
                          $active={step.isActive}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.1 * idx }}
                        >
                          <div className="icon-wrapper"><Icon size={20} /></div>
                          <div className="content">
                            <h4>{step.title}</h4>
                            <p>{step.location || 'In Transit'}</p>
                            <span className="time">{step.time}</span>
                          </div>
                        </TimelineItem>
                      );
                    })}
                  </Timeline>
                </ResultCard>
              )}
            </AnimatePresence>
          </SearchContainer>
        </HeroContent>
      </HeroSection>

      {/* STATS SECTION */}
      <Section $bg="white">
        <Container>
          <StatsGrid>
            <StatItem>
              <Users className="icon" />
              <span className="num">1,000+</span>
              <span className="label">Active Clients</span>
            </StatItem>
            <StatItem>
              <MapPin className="icon" />
              <span className="num">29k+</span>
              <span className="label">Pincodes</span>
            </StatItem>
            <StatItem>
              <Calendar className="icon" />
              <span className="num">5+</span>
              <span className="label">Years Legacy</span>
            </StatItem>
            <StatItem>
              <ShieldCheck className="icon" />
              <span className="num">99.9%</span>
              <span className="label">Safe Delivery</span>
            </StatItem>
          </StatsGrid>
        </Container>
      </Section>

      {/* CONTEXT AWARE: SOLUTIONS */}
      <Section $bg="#F8FAFC">
        <Container>
          <SectionHeader initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
            <h2>Tailored Solutions</h2>
            <p>Whether you are a startup scaling up or an established enterprise, our infrastructure adapts to you.</p>
          </SectionHeader>

          <Grid variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <FeatureCard variants={fadeInUp}>
              <BarChart3 className="icon" />
              <h3>Business Intelligence</h3>
              <p>Get detailed analytics on your supply chain performance, delivery times, and inventory turnover.</p>
            </FeatureCard>
            <FeatureCard variants={fadeInUp}>
              <Warehouse className="icon" />
              <h3>Smart Warehousing</h3>
              <p>Automated storage solutions with real-time API integration for instant stock updates.</p>
            </FeatureCard>
            <FeatureCard variants={fadeInUp}>
              <Globe className="icon" />
              <h3>Hyperlocal & National</h3>
              <p>From bike deliveries in congested cities to 18-wheelers across highways, we cover it all.</p>
            </FeatureCard>
          </Grid>
        </Container>
      </Section>

      {/* DARK MODE: INDUSTRIES */}
      <Section $dark>
        <Container>
          <SectionHeader $dark initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
            <h2>Industries We Serve</h2>
            <p>Specialized handling protocols for sensitive and high-value sectors.</p>
          </SectionHeader>

          <Grid variants={staggerContainer} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <FeatureCard $dark variants={fadeInUp}>
              <Sprout className="icon" />
              <h3>Agri-Tech</h3>
              <p>Cold chain logistics for fresh produce, seeds, and fertilizers reaching remote villages.</p>
            </FeatureCard>
            <FeatureCard $dark variants={fadeInUp}>
              <Pill className="icon" />
              <h3>Healthcare</h3>
              <p>Temperature-controlled transport for vaccines, pharma, and critical medical equipment.</p>
            </FeatureCard>
            <FeatureCard $dark variants={fadeInUp}>
              <ShoppingBag className="icon" />
              <h3>E-Commerce</h3>
              <p>Same-day and Next-day delivery capabilities with integrated return management.</p>
            </FeatureCard>
            <FeatureCard $dark variants={fadeInUp}>
              <Shirt className="icon" />
              <h3>Textiles</h3>
              <p>Bulk transport solutions for raw materials and finished garments to ports and retailers.</p>
            </FeatureCard>
          </Grid>
        </Container>
      </Section>

      {/* --- OUR NETWORK SECTION --- */}
      <NetworkSection>
        <Container>
          <SectionHeader initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
            <h2>Our Network</h2>
            <p>Strategically positioned hubs ensuring rapid connectivity across the region.</p>
          </SectionHeader>
        </Container>

        <NetworkGrid>
          {/* HEAD OFFICE CARD (Left Column) */}
          <HeadOfficeCard
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="badge">HEAD OFFICE</span>
            <h3>{branchData.headOffice.name}</h3>

            <div style={{ marginTop: '40px' }}>
              <div className="detail-row">
                <MapPin className="icon" size={24} />
                <div>
                  <h5>Address</h5>
                  <p>{branchData.headOffice.address}</p>
                </div>
              </div>

              <div className="detail-row">
                <Clock className="icon" size={24} />
                <div>
                  <h5>Opening Hours</h5>
                  <p>{branchData.headOffice.hours}</p>
                </div>
              </div>

              <div className="detail-row">
                <Phone className="icon" size={24} />
                <div>
                  <h5>Contact</h5>
                  <p>{branchData.headOffice.phone}</p>
                </div>
              </div>

              <div className="detail-row">
                <Mail className="icon" size={24} />
                <div>
                  <h5>Email</h5>
                  <p>{branchData.headOffice.email}</p>
                </div>
              </div>
            </div>
          </HeadOfficeCard>

          {/* BRANCH CARDS (Right Columns) */}
          {branchData.branches.map((branch, index) => (
            <BranchCard
              key={branch.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <h4>{branch.name}</h4>
                <Building2 size={20} color="#cbd5e1" />
              </div>

              <div className="mini-detail">
                <MapPin className="icon" />
                <span>{branch.address}</span>
              </div>

              <div className="mini-detail">
                <Phone className="icon" />
                <span>{branch.phone}</span>
              </div>

              <div className="mini-detail">
                <Clock className="icon" />
                <span>{branch.hours}</span>
              </div>
            </BranchCard>
          ))}
        </NetworkGrid>
      </NetworkSection>
      {/* WHY CHOOSE US SECTION */}
      <WhySection>
        <WhyContainer>

          {/* Left: Content */}
          <WhyContent
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Strategic Logistics<br />Partnership</h2>
            <p className="subtitle">
              We don't just move boxes; we analyze, optimize, and execute delivery strategies that give your business a competitive edge.
            </p>

            <FeatureList>
              <FeatureItem whileHover={{ x: 10 }}>
                <div className="icon-box"><Clock size={28} strokeWidth={2} /></div>
                <div>
                  <h4>Precision Timing</h4>
                  <p>Our AI-driven route optimization ensures 98.5% of shipments arrive exactly within the predicted window.</p>
                </div>
              </FeatureItem>

              <FeatureItem whileHover={{ x: 10 }}>
                <div className="icon-box"><ShieldCheck size={28} strokeWidth={2} /></div>
                <div>
                  <h4>Zero-Risk Handling</h4>
                  <p>Every shipment is fully insured. Our specialized handling protocols reduce damage rates to near zero.</p>
                </div>
              </FeatureItem>

              <FeatureItem whileHover={{ x: 10 }}>
                <div className="icon-box"><Users size={28} strokeWidth={2} /></div>
                <div>
                  <h4>Human-Centric Support</h4>
                  <p>Get a dedicated account manager. No automated loops—just real logistics experts solving real problems.</p>
                </div>
              </FeatureItem>
            </FeatureList>
          </WhyContent>

          {/* Right: Image & Badge */}
          <WhyVisual
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <MainImage>
              <img
                src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=2070&auto=format&fit=crop"
                alt="SDD Logistics Dashboard"
              />
            </MainImage>

            {/* Floating Stat Card */}
            <FloatingBadge
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              <div className="stat-icon">
                <CheckCircle size={32} />
              </div>
              <div className="stat-text">
                <h3>1.2M+</h3>
                <span>Parcels Delivered</span>
              </div>
            </FloatingBadge>
          </WhyVisual>

        </WhyContainer>
      </WhySection>

      {/* CTA */}
      <CTASection>
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
          <h2>Ready to optimize your supply chain?</h2>
          <p>Join thousands of businesses moving forward with SDD.</p>
          <CTALink to="/dashboard">
            Get Started Now <ArrowRight size={20} />
          </CTALink>
        </motion.div>
      </CTASection>

    </PageWrapper>
  );
};

export default Home;